import React, { ReactNode, Suspense } from "react";
import ModalAtom from "../../atoms/Modal";
import styled, { css } from "styled-components";
import { FontSizes } from "../../../theme/fonts";
import Separator from "../../atoms/Separator";
import Button from "../../atoms/Button";
import { Colors } from "../../../theme/colors";
import Text from "../../atoms/Text";

type ModalInfoType = "check" | "question";

interface ModalInfoProps {
  isOpen: boolean;
  type?: ModalInfoType;
  title?: string;
  message?: string | ReactNode;
  actions?: ReactNode;
  onRequestClose: () => void;
}

const ModalInfo = ({
  isOpen,
  type,
  title,
  message,
  actions,
  onRequestClose,
}: ModalInfoProps) => {
  return (
    <ModalAtom maxWidth={300} isOpen={isOpen} onRequestClose={onRequestClose}>
      <Container>
        <Header>
          <ButtonStyled
            type="primary-inverted"
            variation="horizontal"
            icon="close"
            onClick={onRequestClose}
          />
        </Header>
        {type !== undefined && (
          <>
            <Separator type="xlarge" />
            <Suspense fallback={null}>
              <IconSVG as={getIconSVG(type)} type={type} />
            </Suspense>
          </>
        )}
        {title !== undefined && (
          <>
            <Separator type="medium" />
            <Text type="h1" style={{ textAlign: "center" }}>
              {title}
            </Text>
          </>
        )}
        {message !== undefined && (
          <>
            <Separator type="medium" />
            {typeof message == "string" ? <Label>{message}</Label> : message}
          </>
        )}
        {actions !== undefined && (
          <>
            <Separator type="xlarge" />
            <Actions>{actions}</Actions>
          </>
        )}
      </Container>
    </ModalAtom>
  );
};

const getIconSVG = (type: ModalInfoType) => {
  switch (type) {
    case "check":
      return CheckSVG;
    case "question":
      return QuestionSVG;
  }
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const IconSVG = styled.div<{ type: ModalInfoType }>`
  ${(props) => {
    let styles = css``;
    switch (props.type) {
      case "check":
        styles = css`
          fill: ${Colors.Success};
        `;
        break;
      case "question":
        styles = css`
          fill: ${Colors.Info};
        `;
        break;
    }
    return styles;
  }}
  max-width: 150px;
  max-height: 150px;
  min-width: 50px;
  min-height: 50px;
  width: 100%;
  align-self: center;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const ButtonStyled = styled(Button)`
  margin-left: ${4 * 2}px;
`;

const Label = styled.div`
  font-size: ${FontSizes.Paragraph};
  text-align: center;
  white-space: pre-line;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: nowrap;

  & > *:not(:last-child) {
    flex: 1;
    margin-right: ${4 * 2}px;
  }

  & > :last-child {
    flex: 2;
  }
`;

const CheckSVG = React.lazy(() => import("../../atoms/Svg/CheckSVG"));
const QuestionSVG = React.lazy(() => import("../../atoms/Svg/QuestionSVG"));

export default ModalInfo;
